/**
 * 텍스트 입력 필드 컴포넌트
 *
 * 디자인 시스템의 input-base 스타일을 활용한 재사용 가능한 입력 필드입니다.
 * forwardRef를 사용하여 DOM 접근이 가능하며, label, 에러 상태, 도움말 텍스트를 지원합니다.
 *
 * @component
 * @example
 * // 기본 입력 필드
 * <AppInput
 *   type="text"
 *   placeholder="사용자 이름"
 *   label="이름"
 *   onChange={(e) => setName(e.target.value)}
 * />
 *
 * @example
 * // 에러 상태와 도움말
 * <AppInput
 *   label="이메일"
 *   type="email"
 *   error={!!emailError}
 *   helperText={emailError || "유효한 이메일을 입력하세요"}
 * />
 *
 * @typedef {object} AppInputProps
 * @property {string} [label] - 입력 필드 위에 표시할 라벨 텍스트
 * @property {boolean} [error=false] - 에러 상태 (true일 때 빨간색으로 표시)
 * @property {string} [helperText] - 입력 필드 아래에 표시할 도움말 또는 에러 메시지
 * @property {string} [className] - 추가 CSS 클래스
 * @property {string} [id] - 입력 필드의 ID (생략 시 label에서 자동 생성)
 *
 * @returns {JSX.Element} 라벨과 도움말을 포함한 입력 필드
 */

import { InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface AppInputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  helperText?: string;
  label?: string;
}

export const AppInput = forwardRef<HTMLInputElement, AppInputProps>(
  ({ error, helperText, label, className, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const helperId = helperText && inputId ? `${inputId}-helper` : undefined;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-neutral-700 mb-1"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error || undefined}
          aria-describedby={helperId}
          className={cn('input-base', error && 'input-error', className)}
          {...props}
        />
        {helperText && (
          <p
            id={helperId}
            className={cn('text-sm mt-1', error ? 'text-error-600' : 'text-neutral-500')}
            role={error ? 'alert' : undefined}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

AppInput.displayName = 'AppInput';
